import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Divider,
  Chip,
  Grid,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  SupervisorAccount as ManagerIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  AccountTree as AccountTreeIcon,
  LocationOn as LocationOnIcon,
  VpnKey as PasswordIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';
import { usersAPI } from '../services/api';

function UserDetailsDialog({ open, onClose, username }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState('');

  useEffect(() => {
    if (open && username) {
      loadUserDetails();
    }
  }, [open, username]);

  const loadUserDetails = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch user details and groups in parallel
      const [userResponse, groupsResponse] = await Promise.all([
        usersAPI.get(username),
        usersAPI.getUserGroups(username)
      ]);

      setUserDetails(userResponse.data);
      setUserGroups(groupsResponse.data.groups || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredGroups = () => {
    if (!groupFilter) return userGroups;
    return userGroups.filter(group =>
      group.toLowerCase().includes(groupFilter.toLowerCase())
    );
  };

  const getFieldValue = (field) => {
    if (!field) return null;
    return Array.isArray(field) ? field[0] : field;
  };

  const formatPasswordLastSet = (pwdLastSet) => {
    if (!pwdLastSet || pwdLastSet === '0') return 'Never';

    try {
      // Windows FILETIME: 100-nanosecond intervals since January 1, 1601
      // Convert to milliseconds since Unix epoch (January 1, 1970)
      // eslint-disable-next-line no-undef
      const windowsEpoch = 116444736000000000n; // 100-nanosecond intervals
      // eslint-disable-next-line no-undef
      const fileTime = BigInt(pwdLastSet);

      // eslint-disable-next-line no-undef
      if (fileTime === 0n) return 'Never';

      // Convert to Unix timestamp in milliseconds
      const unixTimestamp = Number((fileTime - windowsEpoch) / 10000n);
      const date = new Date(unixTimestamp);

      // Format the date
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      // If conversion fails, try to parse as ISO date string
      try {
        const date = new Date(pwdLastSet);
        if (isNaN(date.getTime())) return 'Unknown';
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      } catch {
        return 'Unknown';
      }
    }
  };

  const InfoRow = ({ icon, label, value }) => (
    <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
      <Grid item xs={12} sm={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body1">{value || '-'}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          User Details
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : userDetails ? (
          <Box sx={{ py: 2 }}>
            {/* Basic Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <InfoIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1.25rem' }} />
              Basic Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <InfoRow
                icon={<PersonIcon fontSize="small" />}
                label="Username"
                value={getFieldValue(userDetails.sAMAccountName) || userDetails.username}
              />
              <InfoRow
                icon={<PersonIcon fontSize="small" />}
                label="Display Name"
                value={getFieldValue(userDetails.displayName) || getFieldValue(userDetails.cn)}
              />
              <InfoRow
                icon={<PersonIcon fontSize="small" />}
                label="First Name"
                value={getFieldValue(userDetails.givenName) || userDetails.given_name}
              />
              <InfoRow
                icon={<PersonIcon fontSize="small" />}
                label="Surname"
                value={getFieldValue(userDetails.sn) || userDetails.surname}
              />
              <InfoRow
                icon={<InfoIcon fontSize="small" />}
                label="Description"
                value={getFieldValue(userDetails.description)}
              />
              <InfoRow
                icon={<AccountTreeIcon fontSize="small" />}
                label="User Principal Name"
                value={getFieldValue(userDetails.userPrincipalName)}
              />
            </Paper>

            {/* Contact Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <EmailIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1.25rem' }} />
              Contact Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <InfoRow
                icon={<EmailIcon fontSize="small" />}
                label="Email"
                value={getFieldValue(userDetails.mail)}
              />
              <InfoRow
                icon={<PhoneIcon fontSize="small" />}
                label="Phone"
                value={getFieldValue(userDetails.telephoneNumber)}
              />
              <InfoRow
                icon={<PhoneIcon fontSize="small" />}
                label="Mobile"
                value={getFieldValue(userDetails.mobile)}
              />
              <InfoRow
                icon={<LocationOnIcon fontSize="small" />}
                label="Address"
                value={getFieldValue(userDetails.streetAddress) || getFieldValue(userDetails.postalAddress)}
              />
              <InfoRow
                icon={<LocationOnIcon fontSize="small" />}
                label="City"
                value={getFieldValue(userDetails.l)}
              />
              <InfoRow
                icon={<LocationOnIcon fontSize="small" />}
                label="State/Province"
                value={getFieldValue(userDetails.st)}
              />
              <InfoRow
                icon={<LocationOnIcon fontSize="small" />}
                label="Postal Code"
                value={getFieldValue(userDetails.postalCode)}
              />
              <InfoRow
                icon={<LocationOnIcon fontSize="small" />}
                label="Country"
                value={getFieldValue(userDetails.co) || getFieldValue(userDetails.c)}
              />
            </Paper>

            {/* Organizational Information */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <BusinessIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1.25rem' }} />
              Organizational Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <InfoRow
                icon={<BadgeIcon fontSize="small" />}
                label="Title"
                value={getFieldValue(userDetails.title)}
              />
              <InfoRow
                icon={<BadgeIcon fontSize="small" />}
                label="Role"
                value={getFieldValue(userDetails.role)}
              />
              <InfoRow
                icon={<BusinessIcon fontSize="small" />}
                label="Department"
                value={getFieldValue(userDetails.department)}
              />
              <InfoRow
                icon={<BusinessIcon fontSize="small" />}
                label="Company"
                value={getFieldValue(userDetails.company)}
              />
              <InfoRow
                icon={<ManagerIcon fontSize="small" />}
                label="Manager"
                value={getFieldValue(userDetails.manager)}
              />
              <InfoRow
                icon={<AccountTreeIcon fontSize="small" />}
                label="Organizational Unit"
                value={userDetails.ou}
              />
              <InfoRow
                icon={<AccountTreeIcon fontSize="small" />}
                label="Distinguished Name"
                value={getFieldValue(userDetails.distinguishedName) || userDetails.dn}
              />
            </Paper>

            {/* Account Status */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <PasswordIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '1.25rem' }} />
              Account Status & Security
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Status
                </Typography>
                <Chip
                  label={userDetails.enabled ? 'Enabled' : 'Disabled'}
                  color={userDetails.enabled ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
              <InfoRow
                icon={<TimeIcon fontSize="small" />}
                label="Password Last Changed"
                value={formatPasswordLastSet(getFieldValue(userDetails.pwdLastSet))}
              />
              <InfoRow
                icon={<PersonIcon fontSize="small" />}
                label="Distinguished Name"
                value={userDetails.dn}
              />
            </Paper>

            {/* Group Memberships */}
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupsIcon />
                Group Memberships ({userGroups.length})
              </Box>
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              {userGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Not a member of any groups
                </Typography>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Filter groups..."
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  {getFilteredGroups().length === 0 ? (
                    <Alert severity="warning">
                      No groups match the filter "{groupFilter}"
                    </Alert>
                  ) : (
                    <List dense>
                      {getFilteredGroups().map((group, index) => (
                        <ListItem
                          key={index}
                          divider={index < getFilteredGroups().length - 1}
                          sx={{ pl: 0 }}
                        >
                          <GroupsIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1.2rem' }} />
                          <ListItemText primary={group} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}
            </Paper>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserDetailsDialog;
